'use client';

import { Box } from '@terra/ui-styled-system';
import { BodyDefault } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { useHomeVisitBaseline } from '../../hooks/dashboard/useHomeVisitBaseline';
import { useKpiMetrics } from '../../hooks/dashboard/useKpiMetrics';
import { useNeedsAttention } from '../../hooks/dashboard/useNeedsAttention';
import { usePersistTargetVisit } from '../../hooks/usePersistTargetVisit';
import { useAppTargetId, useTargetType } from '../../store/projectStore';
import { ActivityTrendCard } from './ActivityTrendCard';
import { HomePageLayout } from './HomePageLayout';
import { HomeScanningStatusPill } from './HomeScanningStatusPill';
import { HomeWorkGrid } from './HomeWorkGrid';
import { PostureStrip } from './PostureStrip';
import { SinceLastVisitStrip } from './SinceLastVisitStrip';
import { TargetProfileCard } from './TargetProfileCard';
import { TargetReadinessCard } from './TargetReadinessCard';
import { WelcomeHeader } from './WelcomeHeader';

export function PentesterDashboard() {
  const t = useTranslations('home');
  const appTargetId = useAppTargetId();
  const targetType = useTargetType();
  const { data: metrics } = useKpiMetrics(appTargetId ?? '');
  const { baselineAt, isReady: isBaselineReady } = useHomeVisitBaseline({
    targetId: appTargetId ?? null,
    targetType,
  });
  const attention = useNeedsAttention({
    applicationId: appTargetId ?? '',
    targetType,
  });

  const visitSnapshot = useMemo(() => {
    if (!appTargetId || !metrics) {
      return null;
    }

    return {
      signalsCount: metrics.signals.total,
      findingsCount: metrics.findings.total,
      assetsCount: metrics.assets.total,
      executionsCount: metrics.executions.total,
    };
  }, [appTargetId, metrics]);

  usePersistTargetVisit({
    targetId: appTargetId ?? null,
    targetType,
    snapshot: visitSnapshot,
  });

  if (!appTargetId) {
    return (
      <Box
        h="full"
        w="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <BodyDefault color="fg.neutral.muted">
          {t('selectApplication')}
        </BodyDefault>
      </Box>
    );
  }

  return (
    <HomePageLayout>
      <WelcomeHeader
        lastVisitAt={isBaselineReady ? baselineAt : undefined}
        status={<HomeScanningStatusPill applicationId={appTargetId} />}
      />
      <SinceLastVisitStrip
        applicationId={appTargetId}
        baselineAt={baselineAt}
        isBaselineReady={isBaselineReady}
      />
      <HomeWorkGrid
        applicationId={appTargetId}
        attentionItems={attention.items}
        isAttentionLoading={attention.isLoading}
      />
      <PostureStrip applicationId={appTargetId} />
      <TargetReadinessCard applicationId={appTargetId} />
      <ActivityTrendCard applicationId={appTargetId} />
      <TargetProfileCard applicationId={appTargetId} />
    </HomePageLayout>
  );
}
